import { useCallback, useEffect, useRef, useState } from 'react'

interface UseQueueVoiceAnnouncementOptions {
  repeatCount?: number
  repeatDelayMs?: number
  defaultMuted?: boolean
}

const DEFAULT_REPEAT_COUNT = 2
const DEFAULT_REPEAT_DELAY_MS = 1200

const getThaiVoice = (
  voices: SpeechSynthesisVoice[]
): SpeechSynthesisVoice | null => {
  return voices.find(voice => voice.lang.toLowerCase().startsWith('th')) ?? null
}

const useQueueVoiceAnnouncement = (options?: UseQueueVoiceAnnouncementOptions) => {
  const repeatCount = options?.repeatCount ?? DEFAULT_REPEAT_COUNT
  const repeatDelayMs = options?.repeatDelayMs ?? DEFAULT_REPEAT_DELAY_MS
  const defaultMuted = options?.defaultMuted ?? true

  const [isVoiceSupported, setIsVoiceSupported] = useState<boolean>(false)
  const [isVoiceMuted, setIsVoiceMuted] = useState<boolean>(defaultMuted)
  const [isVoiceSpeaking, setIsVoiceSpeaking] = useState<boolean>(false)
  const [voiceErrorText, setVoiceErrorText] = useState<string>('')
  const [speakingAnnouncementText, setSpeakingAnnouncementText] =
    useState<string>('')

  const synthRef = useRef<SpeechSynthesis | null>(null)
  const voicesRef = useRef<SpeechSynthesisVoice[]>([])
  const speechTokenRef = useRef<number>(0)
  const delayTimerRef = useRef<number | null>(null)

  const clearDelayTimer = () => {
    if (delayTimerRef.current === null) return
    window.clearTimeout(delayTimerRef.current)
    delayTimerRef.current = null
  }

  const stopCurrentSpeech = useCallback(() => {
    speechTokenRef.current += 1
    clearDelayTimer()

    if (synthRef.current) {
      synthRef.current.cancel()
    }

    setIsVoiceSpeaking(false)
    setSpeakingAnnouncementText('')
  }, [])

  const speakOnce = useCallback((text: string, onEnd?: () => void) => {
    const synth = synthRef.current
    if (!synth) return

    const utterance = new SpeechSynthesisUtterance(text)
    const selectedVoice = getThaiVoice(voicesRef.current)

    if (selectedVoice) {
      utterance.voice = selectedVoice
    }

    utterance.lang = selectedVoice?.lang ?? 'th-TH'
    utterance.rate = 1
    utterance.pitch = 1
    utterance.volume = 1

    utterance.onstart = () => {
      setIsVoiceSpeaking(true)
    }

    utterance.onend = () => {
      setIsVoiceSpeaking(false)
      if (onEnd) {
        onEnd()
      }
    }

    utterance.onerror = () => {
      setIsVoiceSpeaking(false)
      setSpeakingAnnouncementText('')
      setVoiceErrorText('Unable to play voice announcement on this browser.')
    }

    synth.speak(utterance)
  }, [])

  const speakAnnouncement = useCallback(
    (text: string) => {
      if (!isVoiceSupported || isVoiceMuted) return
      if (!text.trim()) return

      setVoiceErrorText('')
      stopCurrentSpeech()
      setSpeakingAnnouncementText(text)

      const nextToken = speechTokenRef.current
      const safeRepeatCount = Math.max(1, repeatCount)

      const runRound = (round: number) => {
        if (speechTokenRef.current !== nextToken) return

        speakOnce(text, () => {
          if (speechTokenRef.current !== nextToken) return

          const hasNextRound = round + 1 < safeRepeatCount
          if (!hasNextRound) {
            setSpeakingAnnouncementText('')
            return
          }

          delayTimerRef.current = window.setTimeout(() => {
            runRound(round + 1)
          }, repeatDelayMs)
        })
      }

      runRound(0)
    },
    [isVoiceMuted, isVoiceSupported, repeatCount, repeatDelayMs, speakOnce, stopCurrentSpeech]
  )

  const onToggleVoiceMute = useCallback(() => {
    setIsVoiceMuted(prev => {
      const next = !prev
      if (next) {
        stopCurrentSpeech()
      }
      return next
    })
  }, [stopCurrentSpeech])

  const onTestVoiceCall = useCallback(() => {
    speakAnnouncement('เชิญหมายเลข 10')
  }, [speakAnnouncement])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const hasSpeechSynthesis =
      'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window

    setIsVoiceSupported(hasSpeechSynthesis)
    if (!hasSpeechSynthesis) return

    const synth = window.speechSynthesis
    synthRef.current = synth

    const loadVoices = () => {
      voicesRef.current = synth.getVoices()
    }

    loadVoices()
    synth.addEventListener('voiceschanged', loadVoices)

    return () => {
      stopCurrentSpeech()
      synth.removeEventListener('voiceschanged', loadVoices)
    }
  }, [stopCurrentSpeech])

  return {
    isVoiceSupported,
    isVoiceMuted,
    isVoiceSpeaking,
    voiceErrorText,
    speakingAnnouncementText,
    onToggleVoiceMute,
    onTestVoiceCall,
    speakAnnouncement
  }
}

export default useQueueVoiceAnnouncement
